import React, {
  FC, ReactElement, ReactNode, useCallback, useMemo, useRef, useState, Children, useEffect,
} from 'react';
import { MainLoader, requestResourceByName, useScopedSelector, DataErrorMessage } from 'ia-react-core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getInstabilityMessages } from 'omni-package-react-cms';
import ResourcesContext, { ResourceContextInfo } from '../ResourcesContext/ResourcesContext';
import ErrorMessage from './ErrorMessage';
import usePageLoaded from '../PageLoaded/usePageLoaded';

export const Resources = ({ children }: { children?: ReactNode }) => children as ReactElement;
Resources.type = Symbol('ResourcesLoader.Resources');

export const Content = ({ children }: { children?: ReactNode }) => children as ReactElement;
Content.type = Symbol('ResourcesLoader.Content');

export type ResourcesLoaderStatics = {
  Resources: typeof Resources;
  Content: typeof Content;
};

export interface ResourcesLoaderProps {
  /** Content of the layout. */
  children?: ReactNode;
  bufferingTimeout?: number;
  errorMarginBottom?: number;
  failIfAnyEssentialInError?: boolean;
  showIfAtLeastOneEssential?: boolean;
  hideErrorsIfInstabilityMessages?: boolean;
  validateAvailability?: boolean;
  validateAuthorization?: boolean;
  reloadEveryTime?: boolean;
}

const ResourcesLoader: FC<ResourcesLoaderProps> & ResourcesLoaderStatics = ({ children, bufferingTimeout, errorMarginBottom = 24, showIfAtLeastOneEssential, failIfAnyEssentialInError, hideErrorsIfInstabilityMessages, reloadEveryTime, ...props }) => {
  const { t } = useTranslation('ResourcesLoader');
  const [isLoaded, setLoaded] = usePageLoaded();
  const dispatch = useDispatch();
  const resources = useRef<ResourceContextInfo[]>([]);
  const attempts = useRef(1);
  const retrying = useRef(false);
  const [pending, setPending] = useState(!isLoaded);
  const [errorLevel, setErrorLevel] = useState(null as 'critical' | 'handled');
  const language = useScopedSelector('/language.locale');
  const languageRef = useRef(language);

  const [instabilityMessages, setInstabilityMessages] = useState([] as string[]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialIsLoaded = useMemo(() => isLoaded, []);

  useEffect(() => {
    if (hideErrorsIfInstabilityMessages) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getInstabilityMessages().then((msg: string[]) => setInstabilityMessages(msg));
    }

    if (reloadEveryTime || !initialIsLoaded || languageRef.current !== language) {
      resources.current.forEach((res) => {
        dispatch(requestResourceByName(res.name));
      });
      if (!initialIsLoaded && bufferingTimeout) {
        setTimeout(() => {
          setPending(false);
        }, bufferingTimeout);
      }
      if (!initialIsLoaded || languageRef.current !== language) {
        languageRef.current = language;
        setPending(true);
      }
    }
  }, [dispatch, bufferingTimeout, language, hideErrorsIfInstabilityMessages, reloadEveryTime, initialIsLoaded]);

  let resourcesNode: React.ReactNode = null;
  let contentNode: React.ReactNode = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Children.forEach(children, (child: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (child.type === Resources) {
      resourcesNode = child;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (child.type === Content) {
      contentNode = child;
    }
  });

  const registerResource = useCallback((resource: ResourceContextInfo) => {
    if (!resources.current.find((x) => x.name === resource.name)) {
      resources.current.push(resource);
    }
  }, []);

  const validateErrorLevel = useCallback(() => {
    const failedResources = resources.current.filter((x) => x.failed);
    if (failedResources.length === resources.current.length) {
      setErrorLevel('critical');
    } else if (failedResources.length > 1) {
      if (failedResources.filter((x) => x.essential).length > 0) {
        if (showIfAtLeastOneEssential && !resources.current.filter((x) => x.essential).every((x) => x.failed)) {
          setErrorLevel('handled');
        } else {
          setErrorLevel('critical');
        }
      } else {
        setErrorLevel('handled');
      }
    } else if (failedResources.length > 0 && failIfAnyEssentialInError && failedResources.filter((x) => x.essential).length > 0) {
      setErrorLevel('critical');
    } else {
      setErrorLevel(null);
    }
  }, [showIfAtLeastOneEssential, failIfAnyEssentialInError]);

  const onResourceFailure = useCallback((resource: ResourceContextInfo, status: number) => {
    const res = resources.current.find((x) => x.name === resource.name);
    res.failed = true;
    res.succeeded = false;
    res.status = status;
    validateErrorLevel();
  }, [validateErrorLevel]);

  const onResourceSuccess = useCallback((resource: ResourceContextInfo, status: number) => {
    const res = resources.current.find((x) => x.name === resource.name);
    res.failed = false;
    res.succeeded = true;
    res.status = status;
    validateErrorLevel();
  }, [validateErrorLevel]);

  const onResourceComplete = useCallback((resource: ResourceContextInfo) => {
    resources.current.find((x) => x.name === resource.name).completed = true;
    if (resources.current.every((x) => x.completed)) {
      retrying.current = false;
      setPending(false);
      if (resources.current.every((x) => x.succeeded)) {
        setLoaded();
      }
    }
  }, [setLoaded]);

  const onRetry = useCallback(() => {
    resources.current.forEach((res) => {
      if (res.failed) {
        res.completed = false;
        dispatch(requestResourceByName(res.name));
      }
    });
    attempts.current += 1;
    retrying.current = true;
    setPending(true);
  }, [dispatch]);

  const resourceContext = useMemo(() => ({
    registerResource,
    onResourceFailure,
    onResourceSuccess,
    onResourceComplete,
    errorsHandled: errorLevel !== null,
  }), [errorLevel, onResourceComplete, onResourceFailure, onResourceSuccess, registerResource]);

  const loading = pending && !retrying.current;
  return (
    <ResourcesContext.Provider value={resourceContext}>
      <>
        {resourcesNode}
        {loading && <MainLoader />}
        {!loading && errorLevel === 'critical' && (
          <ErrorMessage {...props} resources={resources.current} onRetry={onRetry} canRetry={attempts.current <= 3 || pending} retrying={pending} />
        )}
        {!loading && errorLevel === 'handled' && (!hideErrorsIfInstabilityMessages || (instabilityMessages?.length ?? 0) === 0) && (
          <DataErrorMessage
            style={{ marginBottom: `${errorMarginBottom}px` }}
            messageTitle={t('errorTitle')}
            serverErrorMessage={t('errorMsg')}
            pending={pending}
            inline={false}
            buttonAlign="left"
            error={{ status: 500 }}
            maxAttempts={3}
            attempts={attempts.current}
            onRetry={onRetry}
          />
        )}
        {!loading && errorLevel !== 'critical' && contentNode}
      </>
    </ResourcesContext.Provider>
  );
};

ResourcesLoader.Content = Content;
ResourcesLoader.Resources = Resources;

export default ResourcesLoader;
